import { useId } from 'react';
import type { TFunction } from 'i18next';

const ALL_LANGS = [
  { code: 'es', label: 'ES', aria: 'Español' },
  { code: 'en', label: 'EN', aria: 'English' },
  { code: 'fr', label: 'FR', aria: 'Français' },
  { code: 'de', label: 'DE', aria: 'Deutsch' },
  { code: 'pt', label: 'PT', aria: 'Português' },
] as const;

export type BookingLanguageControlProps = {
  language: string;
  onLanguageChange: (code: string) => void;
  t: TFunction;
  /** Si se omite, se muestran todos los idiomas (p. ej. gym con PT). */
  languages?: readonly string[];
};

/**
 * En viewport estrecho: <select> nativo. Desde ~480px: botones ES/EN/FR/DE.
 */
export function BookingLanguageControl({
  language,
  onLanguageChange,
  t,
  languages,
}: BookingLanguageControlProps) {
  const selectId = useId();
  const langs =
    languages?.length ?
      ALL_LANGS.filter((l) => languages.includes(l.code))
    : [...ALL_LANGS];
  const fallback = langs[0]?.code ?? 'es';
  const selectValue = langs.some((l) => l.code === language) ? language : fallback;

  return (
    <>
      <div className="booking-lang-select-wrap">
        <label className="sr-only" htmlFor={selectId}>
          {t('booking.language_aria')}
        </label>
        <select
          id={selectId}
          className="booking-lang-select"
          value={selectValue}
          onChange={(e) => onLanguageChange(e.target.value)}
          aria-label={t('booking.language_aria')}
        >
          {langs.map(({ code, label, aria }) => (
            <option key={code} value={code} title={aria}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="language-selector booking-lang-tabs" role="group" aria-label={t('booking.language_aria')}>
        {langs.map(({ code, label, aria }) => (
          <button
            key={code}
            type="button"
            className={`lang-btn ${language === code ? 'active' : ''}`}
            onClick={() => onLanguageChange(code)}
            aria-label={aria}
            aria-pressed={language === code}
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );
}
